/**************************************************************************************************/
/*   File: /src/app/stores/effects/authorization/effects.ts                                       */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Thu May 30 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';

/**
 * The Authorization's effects
 */
@Injectable()
export class AuthorizationEffects {

  /**
   * The request authorization
   * Check if user is enabled, in case of is not enabled check if he is pending
   * else he is authorized
   */
  @Effect()
  checkAuthorizationStatus$ = this.actions$.pipe(
    ofType<fromActions.fromAuthorization.CheckAuthorizationStatus>(
      fromActions.fromAuthorization.AuthorizationActionTypes.CHECK_AUTHORIZATION_STATUS
    ),
    switchMap((action: fromActions.fromAuthorization.CheckAuthorizationStatus) =>
      // Check if user is existing
      this.authorizationService.getAuthorizationStatus().pipe(
        map(result =>
          // Check if user is pending
          new fromActions.fromAuthorization.CheckAuthorizationStatusSuccess(
            {
              authorizationStatus: result
            }
          )
        ),
        catchError(error =>
          of(
            new fromActions.fromAuthorization.CheckAuthorizationStatusFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.WARNING,
                  fromShared.enums.Messages.AUTHORIZE_CANNOT_CONTACT,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromAuthorization.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
  * Create the new authorization request.
  * Dispatch send authorization success action if all things gone right, else send authorization fail action
  */
  @Effect()
  createAuthorizationRequest$ = this.actions$.pipe(
    ofType<fromActions.fromAuthorization.SendAuthorization>(
      fromActions.fromAuthorization.AuthorizationActionTypes.SEND_AUTHORIZATION
    ),
    mergeMap((action: fromActions.fromAuthorization.SendAuthorization) =>
      this.authorizationService.createRequest(action.payload.documentId).pipe(
        map((result: fromShared.models.Result) => {

          // Check if user is pending
          if (result.result) {
            return new fromActions.fromAuthorization.SendAuthorizationSuccess(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.INFO,
                  fromShared.enums.Messages.AUTHORIZE_PENDING,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromAuthorization.STATE_NAME
                )
              }
            );
          }

          return new fromActions.fromAuthorization.SendAuthorizationFail(
            {
              message: new fromShared.models.Message(
                fromShared.enums.MessageType.WARNING,
                fromShared.enums.Messages.CANNOT_CREATE_REQUEST,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromAuthorization.STATE_NAME
              )
            }
          );
        }),
        catchError(error =>
          // Send error to manage when newplan backend cannot store the request
          of(
            new fromActions.fromAuthorization.SendAuthorizationFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_CREATE_REQUEST,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromAuthorization.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );
  /**
   * The file upload request effect.
   * If file creation goes well then dispatch file upload action success, else file upload action fail
   */
  @Effect()
  fileUpload$ = this.actions$.pipe(
    ofType<fromActions.fromAuthorization.FileUploadAction>(
      fromActions.fromAuthorization.FileActionTypes.FILE_UPLOAD
    ),
    switchMap((action: fromActions.fromAuthorization.FileUploadAction) =>
      this.fileService.createFile(action.payload.file).pipe(
        map((response: any) =>
          // IMPROVEMENT Here goes the tow step file upload if needed
          new fromActions.fromAuthorization.FileUploadActionSuccess(
            {
              file: action.payload.file,
              documentId: response
            }
          )
        ),
        catchError((failure) =>
          of(
            new fromActions.fromAuthorization.FileUploadActionFail(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_UPLOAD_REQUESTED_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromAuthorization.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
  * The remove file request.
  * If delete document goes well dispatch file removed success else file removed fail.
  * If response from the service has a falsy result then dispatch file removed action fail
  */
  @Effect()
  fileRemove$ = this.actions$.pipe(
    ofType<fromActions.fromAuthorization.FileRemovedAction>(
      fromActions.fromAuthorization.FileActionTypes.FILE_REMOVED
    ),
    switchMap((action: fromActions.fromAuthorization.FileRemovedAction) =>
      this.fileService.deleteDocument(action.payload.id, action.payload.isUploaded).pipe(
        map((response: any) => {

          if (response.result) {
            // IMPROVEMENT Here goes the tow step file upload if needed
            return new fromActions.fromAuthorization.FileRemovedSuccessAction(
              {
                id: action.payload.id
              }
            );
          } else {
            return new fromActions.fromAuthorization.FileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_REMOVE_FILE,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromAuthorization.STATE_NAME
                )
              }
            );
          }

        }),
        catchError((failure) =>
          of(
            new fromActions.fromAuthorization.FileRemovedFailAction(
              {
                message: new fromShared.models.Message(
                  fromShared.enums.MessageType.ERROR,
                  fromShared.enums.Messages.CANNOT_CONTACT_SERVER,
                  fromShared.enums.MessagePositionEnum.TOP,
                  fromStates.fromAuthorization.STATE_NAME
                )
              }
            )
          )
        )
      )
    )
  );

  /**
   * The clear files request.
   * Dispatch a file removed action for each file passed through action.
   */
  @Effect()
  fileClear$ = this.actions$.pipe(
    ofType<fromActions.fromAuthorization.FileClearAction>(
      fromActions.fromAuthorization.FileActionTypes.FILE_CLEAR
    ),
    mergeMap(
      (action: fromActions.fromAuthorization.FileClearAction) => {
        const arrayOfActions = [];
        action.payload.files.forEach((file) => {
          arrayOfActions.push(
            new fromActions.fromAuthorization.FileRemovedAction({
              id: file.id,
              isUploaded: file.isUploaded
            })
          );
        });
        return arrayOfActions;
      }
    )
  );

  /**
   * The constructore
   * @param {Actions} actions$ The actions
   * @param {fromShared.services.AuthorizationService} authorizationService The service for auth
   * @param {fromShared.services.FileService} fileService The file service
   */
  constructor(
    private actions$: Actions,
    private authorizationService: fromShared.services.AuthorizationService,
    private fileService: fromShared.services.FileService
  ) { }
}
